import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import { IconButton, Tooltip } from '@mui/material';
import { useState } from 'react';
import { dataGridSelectStyle } from '../../../../features/kurver/components/UnderkurverList';
import { TEntity } from '../../../../models/entity';
import { PlainSearchOptions, StaticSearchOptions, } from '../../../utils/utilHooks';
import { useEditPanelContext } from '../../components/AdminDataGrid/EditPanelContext';
import { EditorModus } from '../../components/EditDialog/EditDialog';
import { MultiSelectAutocompleteProps } from '../Autocomplete/Autocomplete';
import Controls from '../Controls';
import { GridTemplateSize } from '../Grid/InlineGridTemplate';
import { ModalMultiSelectProps } from './ModalMultiSelect';
import { propsToClassKey } from '@mui/styles';

export type CompoundMultiSelectProps<T extends TEntity> =
    Omit<MultiSelectAutocompleteProps<T>,
        'onChange' | 'label' | 'getOptions' | 'isOpen' | 'close'>
    &
    Omit<ModalMultiSelectProps<T>, 'isOpen' | 'close'>
    &
    (PlainSearchOptions<T> | StaticSearchOptions<T>)
    & { tooltip?: string, btnTooltip?: string, dialogTitle?: string, dialogTableTitle?: string };

export function CompoundMultiSelect<T extends TEntity>({
    value: selected,
    columns,
    options: inputOptions,
    plainSearch, disablePortal,
    title = 'Redigere',
    dialogTitle = '', dialogTableTitle = '',
    pagination,
    isOptionEqualToValue,
    isRowSelectable,
    gridId,
    testId,
    tooltip,
    btnTooltip,
    onChange,
    disableColumnVisibilitySaving,
    hidden,
    ...others
}: CompoundMultiSelectProps<T>) {
    const [openDialogOptions, setOpenDialogOptions] = useState(false);
    const { mode } = useEditPanelContext<TEntity>();
    const { datagridDisabledRow } = dataGridSelectStyle();
    return (
        <>
            <Controls.InlineGridTemplate
                size={[GridTemplateSize.auto, { min: GridTemplateSize.iconBtn, max: GridTemplateSize.iconBtn }]}
                wrap={false}
                hidden={hidden}
            >
                <Controls.AutoCompleteMultiSelect
                    {...(others as MultiSelectAutocompleteProps<T>)}
                    value={selected ?? []}
                    disablePortal={disablePortal}
                    onChange={(e) => onChange(e.target.value)}
                    getOptionDisabled={(val: T) => !(isRowSelectable?.(val as any) ?? true)}
                    test-id={testId + '-inpt'}
                    required={others.required}
                    disableClearable
                    label={title}
                    singleton
                    isOptionEqualToValue={isOptionEqualToValue ?? ((opt: any, val: any) => opt.id === val.id)}
                    disabled={others.disabled || mode === EditorModus.Vis}
                    title={tooltip}
                />
                <Tooltip title={btnTooltip ?? ''}>
                    <IconButton
                        onClick={() => setOpenDialogOptions((prev) => !prev)}
                        size="medium"
                        disabled={others.disabled || mode === EditorModus.Vis}
                        test-id={testId + '-openModel-btn'}
                        centerRipple
                        component={'span'}
                    >
                        <PlaylistAddOutlinedIcon color={others.disabled ? 'disabled' : 'primary'} fontSize="medium" />
                    </IconButton>
                </Tooltip>
            </Controls.InlineGridTemplate>

            {openDialogOptions && (
                <Controls.ModalMultiSelect
                    {...others}
                    disableColumnVisibilitySaving={disableColumnVisibilitySaving}
                    options={inputOptions ?? []}
                    getRowClassName={(params: any) => isRowSelectable && !isRowSelectable?.(params) ? datagridDisabledRow : ''
                    }
                    isRowSelectable={isRowSelectable}
                    pagination={pagination}
                    gridId={gridId}
                    title={dialogTableTitle.length ? dialogTableTitle : title}
                    dialogTitle={dialogTitle}
                    testId={testId}
                    isOptionEqualToValue={isOptionEqualToValue}
                    columns={columns}
                    isOpen={openDialogOptions}
                    close={() => setOpenDialogOptions(false)}
                    value={selected ?? []}
                    onChange={onChange}
                />
            )}
        </>
    );
}
